import type { Feature } from 'geojson'
import { atom } from 'jotai'
import { atomWithLocation } from 'jotai-location'
import { atomWithStorage } from 'jotai/utils'
import type { Filter, Sort } from 'mongodb'
import type { LngLatBounds, MapRef } from 'react-map-gl'
import type { Listing } from '~/models'
import { DataLayerEntry } from '~/models'
import { aOrganization, aUser } from './app'

type DrawnFeature = Feature | null

const aPersistedDrawnFeature = atomWithStorage<Feature | null>(
  'drawnFeature',
  null,
  {
    getItem: (key: string, initialValue: DrawnFeature) => {
      try {
        const item = localStorage.getItem(key)

        return item === null ? initialValue : JSON.parse(item)
      } catch {
        return initialValue
      }
    },
    setItem: (key: string, value: DrawnFeature): void => {
      if (value) {
        const feature: Feature & { ctx: undefined } = {
          ...value,
          ctx: undefined,
        }

        try {
          return localStorage.setItem(key, JSON.stringify(feature))
        } catch {
          //
        }
      }

      localStorage.removeItem(key)
    },
    removeItem: (key: string): void => {
      localStorage.removeItem(key)
    },
  }
)

const aInternalDrawnFeature = atom<DrawnFeature>(null)

export const aDrawnFeature = atom<DrawnFeature, [DrawnFeature], void>(
  (get) => {
    const user = get(aUser)
    const shouldUsePersisted = user?.options?.map?.preserve ?? false

    if (shouldUsePersisted) {
      return get(aPersistedDrawnFeature)
    }

    return get(aInternalDrawnFeature)
  },
  (get, set, update) => {
    set(aInternalDrawnFeature, update)

    const user = get(aUser)
    const shouldPersist = user?.options?.map?.preserve ?? false

    if (shouldPersist) {
      set(aPersistedDrawnFeature, update)
    } else {
      set(aPersistedDrawnFeature, null)
    }
  }
)

export const aMapRef = atom<MapRef | null>(null)
export const aMapDrawRef = atom<MapboxDraw | null>(null)
export const aMapDrawIsOpen = atom(false)

export const aMapStyle = atomWithStorage<'street' | 'satellite'>(
  'mapStyle',
  'street'
)

export const aMapViewState = atom<{
  bounds: LngLatBounds
  lat: number
  lng: number
  zoom: number
} | null>(null)

export const aFiltersPinned = atomWithStorage<boolean>(
  'mapFiltersPinned',
  false
)

export const aCardsCollapse = atomWithStorage<'single' | 'double' | 'none'>(
  'mapCardsCollapse',
  'double'
)

export type SelectedListing = {
  _id: string
  slug: string
  status: string
  type: string
  propertyTypes: string[]
  location: {
    coordinates: number[]
  }
}

export const aSelectedListing = atom<SelectedListing | Listing | null>(null)

export type HoverFeature = {
  address?: string
  lat: number
  lng: number
  name: string
  propertyTypes: string
  status: string
  type: string
  _id: string
}
export const aHoverFeature = atom<HoverFeature | null>(null)
export const aMarkLocation = atom<boolean>(false)
export const aIsMapEasing = atom<boolean>(false)

export const aMapCurrentCount = atom<number | null>(null)
export const aCheckedListings = atom<string[]>([])
export const aHiddenListings = atomWithStorage<string[]>('hiddenListings', [])

export const aDrawMode = atom<string | null>(null)

export const aParcelDataVisible = atomWithStorage<boolean>(
  'parcelDataVisible',
  false
)
export const aParcelDataResidentialVisible = atomWithStorage<boolean>(
  'parcelDataResidentialVisible',
  false
)
export const aVisibleLayers = atom<string[]>([])
export const aHoverDataLayerEntry = atom<DataLayerEntry | null>(null)

export const aVisibleGeographies = atomWithStorage<string[]>(
  'visibleGeographies',
  []
)

// Internal url tracking. Used by aMapFilters to get/set url params
const aMapLocation = atomWithLocation()

export const aDefaultMapFilters = atom((get): MapFiltersType => {
  if (get(aOrganization)?.options.appraisersOnly) {
    return {
      types: ['sale', 'lease'],
      statuses: ['sold', 'leased'],
    }
  }
  return {
    types: ['sale', 'lease'],
    statuses: ['active', 'pending'],
  }
})

export type MapFiltersType = {
  lat?: string
  lng?: string
  z?: string
  b?: string
  p?: string
  search?: string
  types?: string[]
  statuses?: string[]
  propertyTypes?: string[]
  propertySubtypes?: string[]
  submarkets?: string[]
  zoning?: string[]
  prices?: (number | undefined)[]
  caps?: (number | undefined)[]
  rates?: (number | undefined)[]
  bsf?: (number | undefined)[]
  sf?: (number | undefined)[]
  acres?: (number | undefined)[]
  companies?: string[]
  brokers?: string[]
  created?: (string | undefined)[]
  transacted?: (string | undefined)[]
  sort?: string
  postalCodes?: string[]
  cities?: string[]
  counties?: string[]
  states?: string[]

  officeSf?: (number | undefined)[]
  officeCeilingHeight?: (number | undefined)[]
  dockNumber?: (number | undefined)[]
  driveInNumber?: (number | undefined)[]
  trailerParkingSpaces?: (number | undefined)[]
  ceilingHeight?: (number | undefined)[]
  clearHeight?: (number | undefined)[]
  eaveHeight?: (number | undefined)[]
  overheadCranes?: (number | undefined)[]
  railDoors?: (number | undefined)[]
  // lighting? (string | undefined)[]
  mezzanineSf?: (number | undefined)[]
  mezzanineHvac?: string
  warehouseSf?: (number | undefined)[]
  warehouseHvac?: string
  freightDockNumber?: (number | undefined)[]
  // TODO implement boolean filters
  yard?: string
  yardFenced?: string
  yardPaved?: string
}

const exactValueFields = [
  'sort',
  'search',
  'mezzanineHvac',
  'warehouseHvac',
  'yard',
  'yardFenced',
  'yardPaved',
]

const stringFields = ['companies', 'brokers', 'postalCodes', 'propertySubtypes']
const dateFields = ['created', 'transacted']

export const aPersistedFilters = atomWithStorage<MapFiltersType | null>(
  'persistedFilters',
  null
)

export const aMapFilters = atom(
  (get): MapFiltersType => {
    const defaultMapFilters = get(aDefaultMapFilters)
    const user = get(aUser)
    let searchParams =
      get(aMapLocation).searchParams && get(aMapLocation).searchParams!.size > 0
        ? get(aMapLocation).searchParams
        : null
    if (!searchParams && typeof window !== 'undefined') {
      searchParams = new URLSearchParams(window.location.search)
    }

    const shouldUsePersisted =
      !searchParams?.has('savedSearch') && !!user?.options?.map?.preserve

    let filters: { [key: string]: any } = Object.assign({}, defaultMapFilters)

    if (typeof window !== 'undefined' && shouldUsePersisted) {
      try {
        const persistedFilters = JSON.parse(
          localStorage.getItem('persistedFilters') ?? ''
        )

        if (persistedFilters && Object.keys(persistedFilters).length > 0) {
          filters = Object.assign(filters, persistedFilters)
        }
      } catch {
        //
      }
    }

    if (searchParams) {
      for (const [key, value] of searchParams.entries()) {
        if (key === 'savedSearch') {
          continue
        }
        if (['lat', 'lng', 'z', 'b', 'p'].includes(key)) {
          if (value != '0') {
            filters[key] = value
          }
        } else if (value) {
          if (exactValueFields.includes(key)) {
            filters[key] = value
          } else if (dateFields.includes(key)) {
            filters[key] = value.split('_').map((v) => v || undefined)
          } else {
            filters[key] = value.split('_').map((v) => {
              if (stringFields.includes(key)) {
                return v
              }
              const parsed = parseFloat(v)
              return Number.isNaN(parsed) ? v : parsed
            })
          }
        }
      }
    }
    return filters
  },
  (get, set, filters: { [key: string]: any }) => {
    const params = getParamsFromMapFilters(filters)
    const selectedListing = get(aSelectedListing)
    set(aMapLocation, {
      pathname: selectedListing ? `/${selectedListing.slug}` : '/',
      searchParams: new URLSearchParams(params),
    })
  }
)

export function getParamsFromMapFilters(filters: { [key: string]: any }) {
  const params: [string, string][] = []
  // Compare filters to default and exclude if the same
  Object.keys(filters).forEach((key) => {
    if (filters[key] && !['lat', 'lng', 'z', 'b', 'p'].includes(key)) {
      if (Array.isArray(filters[key])) {
        if (filters[key].filter((v?: any) => v).length > 0) {
          if (dateFields.includes(key)) {
            params.push([
              key,
              filters[key].map((v) => v?.split('T')[0]).join('_'),
            ])
          } else {
            params.push([key, filters[key].join('_')])
          }
        }
      } else {
        params.push([key, filters[key]])
      }
    }
  })
  if (typeof filters.b !== 'undefined' && filters.b != 0) {
    params.unshift(['b', filters.b])
  }
  if (typeof filters.p !== 'undefined' && filters.p != 0) {
    params.unshift(['p', filters.p])
  }
  if (typeof filters.z !== 'undefined') {
    params.unshift(['z', filters.z])
  }
  if (
    typeof filters.lat !== 'undefined' &&
    typeof filters.lng !== 'undefined'
  ) {
    params.unshift(['lat', filters.lat])
    params.unshift(['lng', filters.lng])
  }
  return params
}

const getFindRange = (minValue?: number, maxValue?: number) => {
  const f: { $gte?: number; $lte?: number } = {}
  if (minValue) {
    f['$gte'] = minValue
  }
  if (maxValue) {
    f['$lte'] = maxValue
  }
  return Object.keys(f).length === 0 ? undefined : f
}

type MinMaxRange<MinKey extends string, MaxKey extends string, T = number> =
  | { [K in MaxKey]: { $gte: T } }
  | { [K in MinKey]: { $lte: T } }
  | {
      $or: [
        {
          $and: [{ [K in MinKey]: { $gte: T } }, { [K in MinKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MaxKey]: { $gte: T } }, { [K in MaxKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MinKey]: { $lte: T } }, { [K in MaxKey]: { $gte: T } }]
        },
      ]
    }

function getMinMaxRange<MinKey extends string, MaxKey extends string>(
  minKey: MinKey,
  maxKey: MaxKey,
  min?: number,
  max?: number
): MinMaxRange<MinKey, MaxKey> | undefined {
  if (min !== undefined && max !== undefined) {
    return {
      $or: [
        {
          $and: [{ [minKey]: { $gte: min } }, { [minKey]: { $lte: max } }],
        },
        {
          $and: [{ [maxKey]: { $gte: min } }, { [maxKey]: { $lte: max } }],
        },
        {
          $and: [{ [minKey]: { $lte: min } }, { [maxKey]: { $gte: max } }],
        },
      ],
    } as MinMaxRange<MinKey, MaxKey>
  } else if (min) {
    return { [maxKey]: { $gte: min } } as MinMaxRange<MinKey, MaxKey>
  } else if (max) {
    return { [minKey]: { $lte: max } } as MinMaxRange<MinKey, MaxKey>
  }
}

type MapFiltersPriceFind = {
  $or?: {
    $and?: {
      'filters.price'?: { $gte?: number; $lte?: number }
      'filters.priceMax'?: { $gte?: number; $lte?: number }
    }[]
  }[]
  'filters.price'?: { $lte?: number }
  'filters.priceMax'?: { $gte?: number }
}

type MapFiltersSaleFind =
  | {
      type: { $in: string[] }
      // 'filters.price'?: { $gte?: number; $lte?: number }
      'filters.capRate'?: { $gte?: number; $lte?: number }
    }
  | MapFiltersPriceFind

type MapFiltersLeaseRateFind = {
  $or?: {
    $and?: {
      'filters.minRate'?: { $gte?: number; $lte?: number }
      'filters.maxRate'?: { $gte?: number; $lte?: number }

      'filters.minRateHistoric'?: { $gte?: number; $lte?: number }
      'filters.maxRateHistoric'?: { $gte?: number; $lte?: number }
    }[]
    // 'filters.minRate'?: { $gte?: number; $lte?: number }
    // 'filters.maxRate'?: { $gte?: number; $lte?: number }
  }[]
  'filters.maxRate'?: { $gte?: number }
  'filters.minRate'?: { $lte?: number }

  'filters.maxRateHistoric'?: { $gte?: number }
  'filters.minRateHistoric'?: { $lte?: number }
}

type MapFiltersLeaseAvailableSfFind = {
  $or?: {
    $and?: {
      'filters.minSf'?: { $gte?: number; $lte?: number }
      'filters.maxSf'?: { $gte?: number; $lte?: number }

      'filters.minSfHistoric'?: { $gte?: number; $lte?: number }
      'filters.maxSfHistoric'?: { $gte?: number; $lte?: number }
    }[]
    // 'filters.minSf'?: { $gte?: number; $lte?: number }
    // 'filters.maxSf'?: { $gte?: number; $lte?: number }
  }[]
  'filters.maxSf'?: { $gte?: number }
  'filters.minSf'?: { $lte?: number }

  'filters.maxSfHistoric'?: { $gte?: number }
  'filters.minSfHistoric'?: { $lte?: number }
}

type MapFiltersLeaseFind = {
  // lease, saleLease
  type: { $in: string[] }
  $and?: (MapFiltersLeaseRateFind | MapFiltersLeaseAvailableSfFind)[]
}

type MapFiltersTypesFind =
  | {
      $or: (MapFiltersSaleFind | MapFiltersLeaseFind)[]
    }
  | {
      $or?: {
        $and?: {
          'filters.minAcres'?: { $gte?: number; $lte?: number }
          'filters.maxAcres'?: { $gte?: number; $lte?: number }
        }[]
        // 'filters.minAcres'?: { $gte?: number; $lte?: number }
        // 'filters.maxAcres'?: { $gte?: number; $lte?: number }
      }[]
      'filters.maxAcres'?: { $gte?: number }
      'filters.minAcres'?: { $lte?: number }
    }
  | {
      // $or?: {
      //   $and?: {
      //     'filters.minTransactionDate'?: { $gte?: number; $lte?: number }
      //     'filters.maxTransactionDate'?: { $gte?: number; $lte?: number }
      //   }[]
      // }[]
      // 'filters.maxTransactionDate'?: { $gte?: number }
      // 'filters.minTransactionDate'?: { $lte?: number }

      // This is a disaster
      $or: (
        | {
            $and: (
              | {
                  'filters.minTransactionDate': {
                    $gte: string
                  }
                }
              | {
                  'filters.minTransactionDate': {
                    $lte: string
                  }
                }
            )[]
          }
        | {
            $and: (
              | {
                  'filters.maxTransactionDate': {
                    $gte: string
                  }
                }
              | {
                  'filters.maxTransactionDate': {
                    $lte: string
                  }
                }
            )[]
          }
        | {
            $and: (
              | {
                  'filters.minTransactionDate': {
                    $lte: string
                  }
                }
              | {
                  'filters.maxTransactionDate': {
                    $gte: string
                  }
                }
            )[]
          }
      )[]
      'filters.maxTransactionDate'?: string
      'filters.minTransactionDate'?: string
    }
  | {
      'filters.buildingSf'?: { $gte?: number; $lte?: number }
      // Sort
      'filters.price'?: { $ne: null }
      'filters.capRate'?: { $ne: null }
      'filters.acres'?: { $ne: null }
      'filters.bsf'?: { $ne: null }
      'filters.minRate'?: { $ne: null }
      // 'filters.maxRate'?: { $ne: null }
      'filters.minSf'?: { $ne: null }
      // { 'filters.maxSf': { $ne: null } }?,
    }
  | MinMaxRange<'meta.ceilingHeightMin', 'meta.ceilingHeightMax'>
  // Industrial
  | {
      'meta.officeSf'?: { $gte?: number; $lte?: number }
      'meta.officeCeilingHeight'?: { $gte?: number; $lte?: number }
      'meta.dockNumber'?: { $gte?: number; $lte?: number }
      'meta.driveInNumber'?: { $gte?: number; $lte?: number }
      'meta.trailerParkingSpaces'?: { $gte?: number; $lte?: number }
      'meta.eaveHeight': { $gte?: number; $lte?: number }
      'meta.overheadCranes': { $gte?: number; $lte?: number }
      'meta.railDoors': { $gte?: number; $lte?: number }
      'meta.mezzanineSf': { $gte?: number; $lte?: number }
      'meta.warehouseSf': { $gte?: number; $lte?: number }
      'meta.freightDockNumber': { $gte?: number; $lte?: number }
    }
  | MinMaxRange<'meta.clearHeightMin', 'meta.clearHeightMax'>

type MapFiltersFind = Filter<Listing> & {
  $text?: { $search: string; $caseSensitive: boolean }
  status?: { $in: string[] }
  'property.postalCode'?: { $in: string[] }
  'property.city'?: { $in: string[] }
  'property.county'?: { $in: string[] }
  'property.stateAbbr'?: { $in: string[] }
  propertyTypes?: { $in: string[] }
  propertySubtypes?: { $in: string[] }
  'meta.submarket'?: { $in: string[] }
  'meta.zoning'?: { $regex: string }
  'company.tid'?: { $in: string[] }
  'brokers.tid'?: { $in: string[] }

  activeDate?: { $gte?: string; $lte?: string }
  offMarketDate?: { $gte?: string; $lte?: string; $ne?: null } // Use for sort

  // yard?: string //boolean
  // yardFenced?: string //boolean
  // yardPaved?: string //boolean

  // For sort
  'filters.price'?: { $ne: null }
  'filters.maxRate'?: { $ne: null }
  'filters.minRate'?: { $ne: null }
  'filters.maxSf'?: { $ne: null }
  'filters.minSf'?: { $ne: null }
  'filters.maxAcres'?: { $ne: null }
  'filters.minAcres'?: { $ne: null }
  'filters.minTransactionDate'?: { $ne: null } | { $exists: true }
  'filters.maxTransactionDate'?: { $ne: null }

  $and: MapFiltersTypesFind[]
}

function getIndustrialFilters(filters: MapFiltersType) {
  const $and = []
  const clearHeightRange = getMinMaxRange<
    'meta.clearHeightMin',
    'meta.clearHeightMax'
  >(
    'meta.clearHeightMin',
    'meta.clearHeightMax',
    filters.clearHeight?.[0],
    filters.clearHeight?.[1]
  )
  if (clearHeightRange) {
    $and.push(clearHeightRange)
  }

  const find: Record<string, { $gte?: number; $lte?: number }> = {}
  const officeRange = getFindRange(filters.officeSf?.[0], filters.officeSf?.[1])
  if (officeRange) {
    find['meta.officeSf'] = officeRange
  }
  const officeCeilingRange = getFindRange(
    filters.officeCeilingHeight?.[0],
    filters.officeCeilingHeight?.[1]
  )
  if (officeCeilingRange) {
    find['meta.officeCeilingHeight'] = officeCeilingRange
  }
  const officeCeilingHeight = getFindRange(
    filters.officeCeilingHeight?.[0],
    filters.officeCeilingHeight?.[1]
  )
  if (officeCeilingHeight) {
    find['meta.officeCeilingHeight'] = officeCeilingHeight
  }
  const dockNumber = getFindRange(
    filters.dockNumber?.[0],
    filters.dockNumber?.[1]
  )
  if (dockNumber) {
    find['meta.dockNumber'] = dockNumber
  }
  const driveInNumber = getFindRange(
    filters.driveInNumber?.[0],
    filters.driveInNumber?.[1]
  )
  if (driveInNumber) {
    find['meta.driveInNumber'] = driveInNumber
  }
  const trailerParkingSpaces = getFindRange(
    filters.trailerParkingSpaces?.[0],
    filters.trailerParkingSpaces?.[1]
  )
  if (trailerParkingSpaces) {
    find['meta.trailerParkingSpaces'] = trailerParkingSpaces
  }
  const eaveHeight = getFindRange(
    filters.eaveHeight?.[0],
    filters.eaveHeight?.[1]
  )
  if (eaveHeight) {
    find['meta.eaveHeight'] = eaveHeight
  }
  const overheadCranes = getFindRange(
    filters.overheadCranes?.[0],
    filters.overheadCranes?.[1]
  )
  if (overheadCranes) {
    find['meta.overheadCranes'] = overheadCranes
  }
  const railDoors = getFindRange(filters.railDoors?.[0], filters.railDoors?.[1])
  if (railDoors) {
    find['meta.railDoors'] = railDoors
  }
  const mezzanineSf = getFindRange(
    filters.mezzanineSf?.[0],
    filters.mezzanineSf?.[1]
  )
  if (mezzanineSf) {
    find['meta.mezzanineSf'] = mezzanineSf
  }
  const warehouseSf = getFindRange(
    filters.warehouseSf?.[0],
    filters.warehouseSf?.[1]
  )
  if (warehouseSf) {
    find['meta.warehouseSf'] = warehouseSf
  }
  const freightDockNumber = getFindRange(
    filters.freightDockNumber?.[0],
    filters.freightDockNumber?.[1]
  )
  if (freightDockNumber) {
    find['meta.freightDockNumber'] = freightDockNumber
  }

  //   'meta.officeSf'?: { $gte?: number; $lte?: number }
  // 'meta.officeCeilingHeight'?: { $gte?: number; $lte?: number }
  // 'meta.dockNumber'?: { $gte?: number; $lte?: number }
  // 'meta.driveInNumber'?: { $gte?: number; $lte?: number }
  // 'meta.trailerParkingSpaces'?: { $gte?: number; $lte?: number }
  // 'meta.eaveHeight': { $gte?: number; $lte?: number }
  // 'meta.overheadCranes': { $gte?: number; $lte?: number }
  // 'meta.railDoors': { $gte?: number; $lte?: number }
  // 'meta.mezzanineSf': { $gte?: number; $lte?: number }
  // 'meta.warehouseSf': { $gte?: number; $lte?: number }
  // 'meta.freightDockNumber': { $gte?: number; $lte?: number }

  if (Object.keys(find).length > 0) {
    $and.push(find)
  }
  // cast as MapFiltersRangeFind[] to type filter correctly
  return $and.filter((v) => v) //as MapFiltersRangeFind[]
}

function getPriceFind(filters: MapFiltersType): MapFiltersPriceFind {
  if ((filters.prices || []).length) {
    const priceMin = filters.prices![0]
    const priceMax = filters.prices![1]
    const priceMinKey = 'filters.price'
    const priceMaxKey = 'filters.priceMax'
    if (priceMin && priceMax) {
      return {
        $or: [
          {
            $and: [
              { [priceMinKey]: { $gte: priceMin } },
              { [priceMinKey]: { $lte: priceMax } },
            ],
          },
          {
            $and: [
              { [priceMaxKey]: { $gte: priceMin } },
              { [priceMaxKey]: { $lte: priceMax } },
            ],
          },
          {
            $and: [
              { [priceMinKey]: { $lte: priceMin } },
              { [priceMaxKey]: { $gte: priceMax } },
            ],
          },
        ],
      }
    } else if (priceMin) {
      return { [priceMaxKey]: { $gte: priceMin } }
    } else if (priceMax) {
      return { [priceMinKey]: { $lte: priceMax } }
    }
  }
  return {}
}

function getSaleLeaseFilters(filters: MapFiltersType) {
  const $or = []
  if (filters.types!.includes('sale')) {
    const sale: MapFiltersSaleFind = {
      type: { $in: ['sale', 'saleLease'] },
      ...getPriceFind(filters),
    }
    if ((filters.caps || []).length > 0) {
      sale['filters.capRate'] = getFindRange(filters.caps![0], filters.caps![1])
    }
    $or.push(sale)
  }
  if (filters.types!.includes('lease')) {
    const $and = []
    const minRateKey = (filters.statuses || []).includes('leased')
      ? 'filters.minRateHistoric'
      : 'filters.minRate'
    const maxRateKey = (filters.statuses || []).includes('leased')
      ? 'filters.maxRateHistoric'
      : 'filters.maxRate'
    if ((filters.rates || []).length > 0) {
      const minRate = filters.rates![0]
      const maxRate = filters.rates![1]
      if (minRate && maxRate) {
        $and.push({
          $or: [
            // { [minRateKey]: { $gte: minRate, $lte: maxRate } },
            {
              $and: [
                { [minRateKey]: { $gte: minRate } },
                { [minRateKey]: { $lte: maxRate } },
              ],
            },
            {
              $and: [
                { [maxRateKey]: { $gte: minRate } },
                { [maxRateKey]: { $lte: maxRate } },
              ],
            },
            {
              $and: [
                { [minRateKey]: { $lte: minRate } },
                { [maxRateKey]: { $gte: maxRate } },
              ],
            },
          ],
        } as MapFiltersLeaseRateFind)
      } else if (minRate) {
        $and.push({ [maxRateKey]: { $gte: minRate } })
      } else if (maxRate) {
        $and.push({ [minRateKey]: { $lte: maxRate } })
      }
    }

    if ((filters.sf || []).length > 0) {
      const minSf = filters.sf![0]
      const maxSf = filters.sf![1]
      const minSfKey = (filters.statuses || []).includes('leased')
        ? 'filters.minSfHistoric'
        : 'filters.minSf'
      const maxSfKey = (filters.statuses || []).includes('leased')
        ? 'filters.maxSfHistoric'
        : 'filters.maxSf'
      if (minSf && maxSf) {
        $and.push({
          $or: [
            {
              // 'filters.minSf': { $gte: minSf, $lte: maxSf },
              $and: [
                { [minSfKey]: { $gte: minSf } },
                { [minSfKey]: { $lte: maxSf } },
              ],
            },
            {
              $and: [
                { [maxSfKey]: { $gte: minSf } },
                { [maxSfKey]: { $lte: maxSf } },
              ],
            },
            {
              $and: [
                { [minSfKey]: { $lte: minSf } },
                { [maxSfKey]: { $gte: maxSf } },
              ],
            },
          ],
        } as MapFiltersLeaseAvailableSfFind)
      } else if (minSf) {
        $and.push({
          [maxSfKey]: { $gte: minSf },
        } as MapFiltersLeaseRateFind)
      } else if (maxSf) {
        $and.push({
          [minSfKey]: { $lte: maxSf },
        } as MapFiltersLeaseRateFind)
      }
    }

    const lease: MapFiltersLeaseFind = {
      type: { $in: ['lease', 'saleLease'] },
      // $and: $and.length > 0 ? $and : undefined,
    }
    if ($and.length > 0) {
      lease.$and = $and
    }
    $or.push(lease)
  }
  return $or
}

function getAcresFind(filters: MapFiltersType) {
  if ((filters.acres || []).length) {
    const minAcres = filters.acres![0]
    const maxAcres = filters.acres![1]
    const minAcresKey = (filters.statuses || []).includes('leased')
      ? 'filters.minAcresHistoric'
      : 'filters.minAcres'
    const maxAcresKey = (filters.statuses || []).includes('leased')
      ? 'filters.maxAcresHistoric'
      : 'filters.maxAcres'
    if (minAcres && maxAcres) {
      return {
        $or: [
          {
            $and: [
              { [minAcresKey]: { $gte: minAcres } },
              { [minAcresKey]: { $lte: maxAcres } },
            ],
          },
          {
            $and: [
              { [maxAcresKey]: { $gte: minAcres } },
              { [maxAcresKey]: { $lte: maxAcres } },
            ],
          },
          {
            $and: [
              { [minAcresKey]: { $lte: minAcres } },
              { [maxAcresKey]: { $gte: maxAcres } },
            ],
          },
        ],
      }
    } else if (minAcres) {
      return { [maxAcresKey]: { $gte: minAcres } }
    } else if (maxAcres) {
      return { [minAcresKey]: { $lte: maxAcres } }
    }
  }
}

function getTransactionDateFind(filters: MapFiltersType) {
  if ((filters.transacted || []).length) {
    const minTransactionDate = filters.transacted![0]
    const maxTransactionDate = filters.transacted![1]
    const minTransactionDateKey = 'filters.minTransactionDate'
    const maxTransactionDateKey = 'filters.maxTransactionDate'

    if (minTransactionDate && maxTransactionDate) {
      return {
        $or: [
          {
            $and: [
              { [minTransactionDateKey]: { $gte: minTransactionDate } },
              { [minTransactionDateKey]: { $lte: maxTransactionDate } },
            ],
          },
          {
            $and: [
              { [maxTransactionDateKey]: { $gte: minTransactionDate } },
              { [maxTransactionDateKey]: { $lte: maxTransactionDate } },
            ],
          },
          {
            $and: [
              { [minTransactionDateKey]: { $lte: minTransactionDate } },
              { [maxTransactionDateKey]: { $gte: maxTransactionDate } },
            ],
          },
        ],
      }
    } else if (minTransactionDate) {
      return { [maxTransactionDateKey]: { $gte: minTransactionDate } }
    } else if (maxTransactionDate) {
      return { [minTransactionDateKey]: { $lte: maxTransactionDate } }
    }
  }
}

function createDateFilter(dateRange: (string | undefined)[]) {
  const [startDate, endDate] = dateRange

  if (!startDate && !endDate) return undefined

  const dateFilter: { $gte?: string; $lte?: string } = {}

  if (startDate) dateFilter.$gte = startDate.toString().split('T')[0]
  if (endDate) dateFilter.$lte = endDate.toString().split('T')[0]

  return dateFilter
}

export function getFindFromFilters(filters: MapFiltersType) {
  // Others
  const $and: MapFiltersTypesFind[] = [
    { $or: getSaleLeaseFilters(filters) }, // lease and sale specific
  ]
  const acresFind = getAcresFind(filters)
  if (acresFind) {
    $and.push(acresFind)
  }
  if ((filters.bsf || []).length > 0) {
    $and.push({
      'filters.buildingSf': getFindRange(filters.bsf![0], filters.bsf![1]),
    })
  }
  const ceilingHeightRange = getMinMaxRange<
    'meta.ceilingHeightMin',
    'meta.ceilingHeightMax'
  >(
    'meta.ceilingHeightMin',
    'meta.ceilingHeightMax',
    filters.ceilingHeight?.[0],
    filters.ceilingHeight?.[1]
  )
  if (ceilingHeightRange) {
    $and.push(ceilingHeightRange)
  }

  const industrialFilters = getIndustrialFilters(filters)
  if (industrialFilters.length > 0) {
    $and.push(...industrialFilters)
  }

  const transactionDateFind = getTransactionDateFind(filters)
  if (transactionDateFind) {
    $and.push(transactionDateFind)
  }

  const find: MapFiltersFind = {
    // type: { $in: [...filters.types!, 'saleLease'] },
    status: { $in: filters.statuses! },
    $and,
  }

  if (filters.search && filters.search.trim().length > 0) {
    find.$text = { $search: filters.search.trim(), $caseSensitive: false }
  }
  if ((filters.propertyTypes || []).length > 0) {
    find.propertyTypes = { $in: filters.propertyTypes! }
  }
  if ((filters.propertySubtypes || []).length > 0) {
    find.propertySubtypes = { $in: filters.propertySubtypes! }
  }

  if ((filters.submarkets || []).length > 0) {
    find['meta.submarket'] = { $in: filters.submarkets! }
  }

  if ((filters.postalCodes || []).length > 0) {
    find['property.postalCode'] = {
      $in: filters.postalCodes!,
    }
  }
  if ((filters.cities || []).length > 0) {
    find['property.city'] = { $in: filters.cities! }
  }
  if ((filters.counties || []).length > 0) {
    find['property.county'] = { $in: filters.counties! }
  }
  if ((filters.states || []).length > 0) {
    find['property.stateAbbr'] = { $in: filters.states! }
  }
  if ((filters.zoning || []).length > 0) {
    find['meta.zoning'] = { $regex: filters.zoning!.join('|') }
  }
  if ((filters.companies || []).length > 0) {
    find['company.tid'] = { $in: filters.companies! }
  }
  if ((filters.brokers || []).length > 0) {
    find['brokers.tid'] = { $in: filters.brokers! }
  }

  if (filters.sort) {
    if (filters.sort.includes('price')) {
      find['filters.price'] = { $ne: null }
    } else if (filters.sort.includes('rateD')) {
      find['filters.maxRate'] = { $ne: null }
    } else if (filters.sort.includes('rateA')) {
      find['filters.minRate'] = { $ne: null }
    } else if (filters.sort === 'availableSfD') {
      find['filters.maxSf'] = { $ne: null }
    } else if (filters.sort === 'availableSfA') {
      find['filters.minSf'] = { $ne: null }
    } else if (filters.sort.includes('acresD')) {
      find['filters.maxAcres'] = { $ne: null }
    } else if (filters.sort.includes('acresA')) {
      find['filters.minAcres'] = { $ne: null }
    } else if (filters.sort.includes('offMarketD')) {
      find['filters.maxTransactionDate'] = { $ne: null }
    } else if (filters.sort.includes('offMarketA')) {
      find['filters.minTransactionDate'] = { $exists: true }
    }
  }

  if (filters.created) {
    const dateFilter = createDateFilter(filters.created)

    if (dateFilter) {
      find['activeDate'] = dateFilter
    }
  }

  return find
}

export const aFiltersFind = atom((get) => {
  const filters = get(aMapFilters)
  return getFindFromFilters(filters)
})

export const aFiltersSort = atom((get) => {
  const filters = get(aMapFilters)
  return getSortFromFilters(filters)
})

export function getSortFromFilters(filters: MapFiltersType) {
  let sort: Sort = { modified: -1 }
  const sortKeys: { [key: string]: string } = {
    modifiedD: 'modified',
    createdD: 'activeDate',
    offMarketD: 'filters.maxTransactionDate',
    offMarketA: 'filters.minTransactionDate',
    availableSfD: 'filters.maxSf',
    availableSfA: 'filters.minSf',
    priceD: 'filters.price',
    priceA: 'filters.price',
    rateD: 'filters.maxRate',
    rateA: 'filters.minRate',
    acresD: 'filters.maxAcres',
    acresA: 'filters.minAcres',
  }
  if (filters.sort) {
    const direction = filters.sort.slice(filters.sort.length - 1)
    if (
      typeof sortKeys[filters.sort] !== 'undefined' &&
      (direction === 'D' || direction === 'A')
    ) {
      sort = { [sortKeys[filters.sort]]: direction === 'D' ? -1 : 1 }
    }
  }
  return sort
}
